<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { formatDistanceToNow, fromUnixTime } from 'date-fns';
import { dateFnsLocale } from 'dashboard/helper/reportDisplay';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ContactInfoRow from './ContactInfoRow.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ContactDeleteModal from 'dashboard/modules/contact/ContactDeleteModal.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import {
  formatPhoneForDisplay,
  parseContactPhoneNumber,
} from 'dashboard/helper/phoneHelper';

export default {
  components: {
    NextButton,
    ContactInfoRow,
    EditContact,
    Avatar,
    ComposeConversation,
    SocialIcons,
    ContactMergeModal,
    ContactDeleteModal,
    VoiceCallButton,
    InlineInput,
    Popover,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    inbox: {
      type: Object,
      default: null,
    },
  },
  emits: ['panelClose'],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      showEditModal: false,
      isEditingName: false,
      editName: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      currentChat: 'getSelectedChat',
    }),
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;

      const telegram = socialProfiles?.telegram || telegramUsername || '';
      const twitter = socialProfiles?.twitter || twitterScreenName || '';

      return {
        ...(socialProfiles || {}),
        twitter,
        telegram,
      };
    },
    inboxName() {
      return this.inbox?.name || '';
    },
    parsedPhoneNumber() {
      return parseContactPhoneNumber(this.contact.phone_number);
    },
    phoneDisplayValue() {
      return formatPhoneForDisplay(this.contact.phone_number);
    },
    phoneCountryCode() {
      return this.parsedPhoneNumber?.country || '';
    },
    lastActivityLabel() {
      if (!this.contact.last_activity_at) return '';
      return this.relativeTime(this.contact.last_activity_at);
    },
    lastActivityTooltip() {
      if (!this.contact.last_activity_at) return '';
      const locale = this.$i18n.locale.replace('_', '-');
      const date = fromUnixTime(this.contact.last_activity_at).toLocaleString(
        locale,
        {
          dateStyle: 'medium',
          timeStyle: 'short',
        }
      );
      return this.$t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', { date });
    },
    hasContactDetails() {
      return Boolean(
        this.contact.email ||
          this.contact.identifier ||
          this.additionalAttributes.company_name ||
          this.location ||
          this.additionalAttributes.location
      );
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        if (!id || this.contact.contact_inboxes !== undefined) return;

        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
  },
  methods: {
    relativeTime(time) {
      return formatDistanceToNow(fromUnixTime(time), {
        addSuffix: true,
        locale: dateFnsLocale(this.$i18n.locale),
      });
    },
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    openEditFromMenu(hide) {
      hide();
      this.toggleEditModal();
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    startEditingName() {
      this.editName = this.contact.name || '';
      this.isEditingName = true;
      this.$nextTick(() => {
        this.$refs.nameInput?.focus();
      });
    },
    saveNameEdit() {
      if (!this.isEditingName) return;
      this.isEditingName = false;
      const trimmed = this.editName.trim();
      if (trimmed && trimmed !== this.contact.name) {
        this.updateContactField({ name: trimmed });
      }
    },
    cancelNameEdit() {
      this.isEditingName = false;
    },
    onFieldUpdate(field, value) {
      this.updateContactField({ [field]: value });
    },
    async updateContactField(attrs) {
      const contactId = this.contact.id;
      try {
        await this.$store.dispatch('contacts/update', {
          id: contactId,
          ...attrs,
        });
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
        await this.$store.dispatch('contacts/fetchContactableInbox', contactId);
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          const detail = error.contactErrorDetail;
          if (detail) {
            useAlert(detail);
          } else {
            const invalidAttrs = Array.isArray(error.data) ? error.data : [];
            if (invalidAttrs.includes('email')) {
              useAlert(this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'));
            } else if (invalidAttrs.includes('phone_number')) {
              useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
            } else {
              useAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
            }
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(error.message || this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
  },
};
</script>

<template>
  <div class="relative w-full px-4 py-4">
    <div class="flex flex-col w-full text-left rtl:text-right">
      <div class="flex items-start justify-between w-full">
        <Avatar
          v-if="showAvatar"
          :src="contact.thumbnail || ''"
          :name="contact.name || ''"
          :status="contact.availability_status"
          :inbox="inbox"
          :size="48"
          :badge-size="22"
          hide-offline-status
        />
        <a
          :href="contactProfileLink"
          target="_blank"
          rel="noopener nofollow noreferrer"
          :title="$t('CONTACT_PANEL.VIEW_PROFILE')"
          class="inline-flex items-center justify-center size-8 rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2"
        >
          <span class="i-lucide-external-link size-4" />
        </a>
      </div>

      <div class="group/name flex items-start gap-1 min-w-0 w-full mt-3">
        <InlineInput
          v-if="isEditingName"
          ref="nameInput"
          v-model="editName"
          custom-input-class="!text-lg !font-medium !leading-6 !tracking-tight !w-auto max-w-full [field-sizing:content]"
          class="!w-fit min-w-0 flex-1"
          @enter-press="saveNameEdit"
          @escape-press="cancelNameEdit"
          @blur="saveNameEdit"
        />
        <h3
          v-else
          class="min-w-0 flex-1 my-0 text-lg font-medium leading-6 tracking-tight capitalize break-words text-pretty text-n-slate-12 cursor-pointer hover:text-n-slate-12/80"
          :title="$t('CONTACT_PANEL.CLICK_TO_EDIT')"
          @click="startEditingName"
        >
          {{ contact.name }}
        </h3>
        <NextButton
          ghost
          xs
          slate
          icon="i-lucide-pencil"
          :title="$t('CONTACT_PANEL.CLICK_TO_EDIT')"
          class="flex-shrink-0 -mx-1 opacity-0 transition-opacity"
          :class="
            isEditingName
              ? 'invisible'
              : 'group-hover/name:opacity-100 focus-visible:opacity-100'
          "
          @click="startEditingName"
        />
      </div>

      <div
        v-if="inboxName || lastActivityLabel"
        class="flex items-center gap-1.5 min-w-0 mt-1 text-xs text-n-slate-10"
      >
        <span v-if="inboxName" class="truncate">{{ inboxName }}</span>
        <span
          v-if="inboxName && lastActivityLabel"
          aria-hidden="true"
          class="size-0.5 rounded-full bg-n-slate-8"
        />
        <span
          v-if="lastActivityLabel"
          v-tooltip.top="lastActivityTooltip"
          class="shrink-0 cursor-help"
        >
          {{ lastActivityLabel }}
        </span>
      </div>

      <p
        v-if="additionalAttributes.description"
        class="self-start break-words mb-0 mt-2 text-sm leading-5 text-n-slate-11"
      >
        {{ additionalAttributes.description }}
      </p>

      <div v-if="contact.phone_number" class="mt-3 w-full">
        <ContactInfoRow
          :value="phoneDisplayValue"
          :raw-value="contact.phone_number"
          :country-code="phoneCountryCode"
          hide-icon
          icon="call"
          emoji="📞"
          :title="$t('CONTACT_PANEL.PHONE_NUMBER')"
          show-copy
          editable
          @update="value => onFieldUpdate('phone_number', value)"
        />
      </div>

      <div class="flex items-center w-full gap-1 mt-3">
        <div v-if="contact.id" class="flex">
          <ComposeConversation :contact-id="String(contact.id)">
            <template #trigger>
              <NextButton
                v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
                :aria-label="$t('CONTACT_PANEL.NEW_MESSAGE')"
                icon="i-ph-chat-circle-dots"
                slate
                faded
                sm
              />
            </template>
          </ComposeConversation>
        </div>
        <VoiceCallButton
          v-if="contact.id"
          :phone="contact.phone_number || ''"
          :contact-id="contact.id"
          :conversation-id="currentChat?.id"
          icon="i-lucide-phone"
          sm
          faded
          slate
          :tooltip-label="$t('CONTACT_PANEL.CALL')"
          :aria-label="$t('CONTACT_PANEL.CALL')"
        />
        <SocialIcons :social-profiles="socialProfiles" />
        <Popover>
          <NextButton
            v-tooltip.top-end="$t('CONTACT_PANEL.CONTACT_ACTIONS')"
            :aria-label="$t('CONTACT_PANEL.CONTACT_ACTIONS')"
            icon="i-lucide-ellipsis"
            slate
            faded
            sm
          />
          <template #content="{ hide }">
            <div class="flex flex-col w-48 p-1">
              <NextButton
                :label="$t('EDIT_CONTACT.BUTTON_LABEL')"
                icon="i-ph-pencil-simple"
                slate
                ghost
                sm
                start
                class="w-full"
                @click="openEditFromMenu(hide)"
              />
              <ContactMergeModal :primary-contact="contact">
                <template #trigger>
                  <NextButton
                    :label="$t('CONTACT_PANEL.MERGE_CONTACT')"
                    icon="i-ph-arrows-merge"
                    slate
                    ghost
                    sm
                    start
                    class="w-full"
                    :disabled="uiFlags.isMerging"
                  />
                </template>
              </ContactMergeModal>
              <ContactDeleteModal
                v-if="isAdmin"
                :contact="contact"
                @deleted="$emit('panelClose')"
              >
                <template #trigger>
                  <NextButton
                    :label="$t('DELETE_CONTACT.BUTTON_LABEL')"
                    icon="i-ph-trash"
                    ruby
                    ghost
                    sm
                    start
                    class="w-full"
                    :disabled="uiFlags.isDeleting"
                  />
                </template>
              </ContactDeleteModal>
            </div>
          </template>
        </Popover>
      </div>

      <div
        v-if="hasContactDetails"
        class="flex flex-col items-start w-full gap-1.5 mt-3 pt-3 border-t border-n-weak"
      >
        <ContactInfoRow
          v-if="contact.email"
          :href="`mailto:${contact.email}`"
          :value="contact.email"
          icon="mail"
          emoji="✉️"
          :title="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
          show-copy
          editable
          @update="value => onFieldUpdate('email', value)"
        />
        <ContactInfoRow
          v-if="contact.identifier"
          :value="contact.identifier"
          icon="contact-identify"
          emoji="🪪"
          :title="$t('CONTACT_PANEL.IDENTIFIER')"
        />
        <ContactInfoRow
          v-if="additionalAttributes.company_name"
          :value="additionalAttributes.company_name"
          icon="building-bank"
          emoji="🏢"
          :title="$t('CONTACT_PANEL.COMPANY')"
          editable
          @update="
            value =>
              updateContactField({
                additional_attributes: {
                  ...additionalAttributes,
                  company_name: value,
                },
              })
          "
        />
        <ContactInfoRow
          v-if="location || additionalAttributes.location"
          :value="location || additionalAttributes.location"
          icon="map"
          emoji="🌍"
          :title="$t('CONTACT_PANEL.LOCATION')"
        />
      </div>

      <EditContact
        :show="showEditModal"
        :contact="contact"
        @cancel="toggleEditModal"
      />
    </div>
  </div>
</template>
